/*
  Nebula cart drawer: loading UI only (snippets/cart-drawer.liquid). Does not change how the cart
  works: Dawn's product-form.js, cart.js and cart-drawer.js still do every request and render.
  A PC line's "Includes N components" dropdown is in the drawer HTML (snippets/nebula-cart-build-
  parts.liquid). The apps (ready-to-ship PCs: rts-cart.js; Custom PC Builder: nb-cart.js) used to
  add it after their own /cart.js request, and rts-cart.js first fetches the whole /cart page for
  a product it has not seen yet, so the raw part list showed for seconds. They still fold any line
  the theme did not.
  This file only OBSERVES those and shows a skeleton while the drawer is not ready:
  - Add to cart (any <product-form> posting to /cart/add): the drawer opens straight away with a
    skeleton, instead of waiting for the response. If the add fails (no new cart content arrives),
    the skeleton goes and the drawer closes again, so the product form's own error stays visible.
  - Quantity change / remove inside the drawer (cart.js sets .cart__items--disabled): skeleton
    over the items until the new contents are in.
  - After new contents arrive, the skeleton stays while a line still shows a raw part list that an
    app has yet to fold, at most FOLD_WAIT ms.
  Real cart content stays in the DOM (hidden with visibility, not display:none), so the app's own
  layout checks keep working underneath.
  The loading state is a data attribute, not a class: the shipping-protection snippet
  (brewapp-protection-checkout.liquid) posts /cart/update.js on every class change of the open drawer.
*/
(() => {
  if (window.__nebulaCartLoading) return;
  window.__nebulaCartLoading = true;

  const LOG = '[nebula-cart]';
  const MIN_SHOW = 350; // ms: avoid a flicker on very fast responses
  const FOLD_WAIT = 2000; // ms after new content: max wait for the app's fold
  const MAX_LOADING = 12000; // ms: safety net, never leave the skeleton up

  const drawer = document.querySelector('cart-drawer');
  const tpl = document.getElementById('NebulaCartSkeletonRow');
  if (!drawer || !tpl) {
    if (!drawer) return; // cart type is not "drawer": nothing to do
    console.warn(LOG, 'skeleton template #NebulaCartSkeletonRow missing');
    return;
  }

  let loading = false;
  let arrived = false; // new drawer contents rendered since start()
  let arrivedAt = 0;
  let shownAt = 0;
  let rows = 1;
  let openedHere = false;
  let maxTimer = 0;
  let settleTimer = 0;

  const countRows = () => drawer.querySelectorAll('.cart-item').length;

  const ensureSkeleton = () => {
    if (!loading) return;
    const inner = drawer.querySelector('.drawer__inner');
    if (!inner || inner.querySelector(':scope > .nebula-cart-skeleton')) return;
    const box = document.createElement('div');
    box.className = 'nebula-cart-skeleton';
    box.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < rows; i++) box.append(tpl.content.firstElementChild.cloneNode(true));
    const header = inner.querySelector(':scope > .drawer__header');
    if (header) header.after(box);
    else inner.prepend(box);
  };

  const removeSkeleton = () => drawer.querySelectorAll('.nebula-cart-skeleton').forEach((el) => el.remove());

  // A build line still showing its raw component list (2+ visible <dt> not yet folded by the app).
  const foldPending = () =>
    [...drawer.querySelectorAll('.cart-item')].some((row) => {
      if (row.querySelector('.nb-cart-parts')) return false;
      const dts = [...row.querySelectorAll('dl dt')].filter((dt) => !dt.hidden && !dt.parentElement.hidden);
      return dts.length >= 2;
    });

  const stop = () => {
    if (!loading) return;
    const wait = shownAt + MIN_SHOW - Date.now();
    if (wait > 0) {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(stop, wait);
      return;
    }
    loading = false;
    clearTimeout(maxTimer);
    clearTimeout(settleTimer);
    delete drawer.dataset.nebulaLoading;
    drawer.removeAttribute('aria-busy');
    removeSkeleton();
  };

  const settle = () => {
    clearTimeout(settleTimer);
    if (!loading || !arrived) return;
    if (foldPending() && Date.now() - arrivedAt < FOLD_WAIT) {
      settleTimer = setTimeout(settle, 80);
      return;
    }
    stop();
  };

  const start = (kind) => {
    rows = Math.min(3, Math.max(1, countRows()));
    arrived = false;
    if (!loading) {
      loading = true;
      shownAt = Date.now();
      drawer.setAttribute('aria-busy', 'true');
    }
    drawer.dataset.nebulaLoading = kind;
    ensureSkeleton();
    clearTimeout(maxTimer);
    maxTimer = setTimeout(() => { arrived = true; stop(); }, MAX_LOADING);
  };

  // The request finished without new contents (error, sold out, quantity limit): back to normal.
  const abort = () => {
    if (!loading || arrived) return;
    stop();
    if (openedHere && drawer.classList.contains('active')) drawer.close();
    openedHere = false;
  };

  // New drawer contents (renderContents replaces #CartDrawer; cart.js replaces .drawer__inner).
  const isRender = (node) =>
    node.nodeType === 1 && !node.classList.contains('nebula-cart-skeleton') &&
    (node.matches('.drawer__inner, .drawer__header, cart-drawer-items, .drawer__inner-empty, .cart-item') ||
      node.querySelector('.drawer__header, cart-drawer-items, .drawer__inner-empty'));

  new MutationObserver((list) => {
    let rendered = false;
    let itemsBusy = null;
    for (const m of list) {
      if (m.type === 'childList' && [...m.addedNodes].some(isRender)) rendered = true;
      if (m.type === 'attributes' && m.target.id === 'CartDrawer-CartItems') {
        itemsBusy = m.target.classList.contains('cart__items--disabled');
      }
    }
    if (itemsBusy === true && !loading) start('update');
    if (rendered && loading) {
      arrived = true;
      arrivedAt = Date.now();
      openedHere = false;
    }
    if (loading) {
      ensureSkeleton();
      if (arrived) settle();
      else if (itemsBusy === false) abort(); // cart.js finished without re-rendering (error)
    }
  }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  // Add to cart from any theme product form (main button, sticky bar, accessory cards).
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.action)) return;
    const productForm = form.closest('product-form');
    if (!productForm) return;
    const button = productForm.querySelector('[type="submit"]');
    // product-form.js ignores submits while its button is disabled / busy.
    if (!button || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('loading')) return;

    // Let product-form.js handle the submit first, then check it really started a request.
    setTimeout(() => {
      if (!button.classList.contains('loading')) return;
      start('add');
      if (!drawer.classList.contains('active')) {
        openedHere = true;
        drawer.open();
      }
      const done = new MutationObserver(() => {
        if (button.classList.contains('loading')) return;
        done.disconnect();
        // Success re-renders the drawer (arrived); give it a moment, otherwise treat it as an error.
        setTimeout(() => { if (!arrived) abort(); }, 300);
      });
      done.observe(button, { attributes: true, attributeFilter: ['class'] });
    }, 0);
  }, true);
})();
